<template>
  <b-form-group
    :id="'group' + formGroupId"
    :class="`text-input-group ${requireError ? 'require-field' : ''}`"
    :label-for="formGroupId"
    :label="`${$translate(placeholder)} ${required ? '*' : ''}`"
    :state="!isError"
    :invalid-feedback="errorMessage"
  >
    <div class="wrapper" @drop="drop" @dragover="allowDrop" @dragleave="dragleave" @dragenter="allowDrop">
      <b-form-file
        :id="formGroupId"
        ref="file"
        v-model="inputValue"
        :name="$attrs.name || $translate(placeholder)"
        :placeholder="choosePlaceholder"
        :drop-placeholder="'drop_file_placeholder' | i18n"
        :browse-text="'browse' | i18n"
        :accept="fileExtensions"
        size="sm"
        class="input"
        :disabled="viewMode"
        :directory="directory"
        :multiple="multiple"
        no-traverse
        :no-drop="noDrop"
        @input="updateValue"
      />
    </div>
    <b-form-text v-if="bottomText">
      {{ bottomText }}
    </b-form-text>
  </b-form-group>
</template>

<script>
import { BFormFile } from 'bootstrap-vue';
import { NotificationMixin } from '../../../../mixins';

export default {
  name: 'FileInput',
  components: { BFormFile },
  mixins: [NotificationMixin],
  props: {
    bottomText: {
      type: String,
      default: '',
    },
    formGroupId: {
      type: String,
      default: null,
    },
    value: {
      type: [String, File, Array],
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    viewMode: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: '',
    },
    fileExtensions: {
      type: String,
      default: '',
    },
    updateValue: {
      type: Function,
      default: () => {},
    },
    updateError: {
      type: Function,
      default: () => {},
    },
    directory: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    noDrop: {
      type: Boolean,
      default: false,
    },
  },
  data: function () {
    return {
      inputValue: null,
      dropIntervalId: {},
      showDropFile: false,
    };
  },
  computed: {
    isError: function () {
      const error = this.error;
      this.updateError(error);
      return error;
    },
    requireError: function () {
      return this.required && !this.inputValue;
    },
    choosePlaceholder: function () {
      return this.inputValue && this.inputValue.name
        ? this.inputValue.name
        : this.$translate(this.noDrop ? 'choose_noDrop_placeholder' : 'choose_file_placeholder');
    },
  },
  watch: {
    inputValue(newVal) {
      this.$emit('input', newVal);
    },
    value(newVal) {
      this.inputValue = newVal;
    },
  },
  mounted() {
    this.inputValue = this.value;
  },
  methods: {
    hideDrop(e) {
      if (this.dropIntervalId.id) {
        clearTimeout(this.dropIntervalId.id);
      }
      this.dropIntervalId.id = setTimeout(() => {
        this.showDropFile = false;
      }, 10);
    },
    drop(event) {
      event.preventDefault();
      this.hideDrop(event);
      const alowedFormats = ['image/png', 'image/jpeg'];
      const file = event.dataTransfer.files[0];
      if (!file.type || alowedFormats.indexOf(file.type) === -1) {
        this.showNotification({ type: 'error', title: this.$translate('upload'), message: this.$translate('format_not_allowed') });
        return;
      }
      this.inputValue = file;
    },
    allowDrop(event) {
      event.preventDefault();
      if (this.dropIntervalId.id) {
        clearTimeout(this.dropIntervalId.id);
      }
      this.showDropFile = true;
    },
    dragleave(event) {
      event.preventDefault();
      this.hideDrop(event);
    },
    setFocus() {
      this.$refs[this.formGroupId].$el.focus();
    },
  },
};
</script>

<style scoped lang="scss">
.wrapper {
  display: flex;
}
.text-input-group {
  position: relative;
}
.text-input-group.require-field,
.text-input-group.is-invalid {
  color: #ed3232;
}
::v-deep .d-block {
  font-size: 16px;
  line-height: 20px;
  margin-bottom: 8px;
}
::v-deep .custom-file {
  height: auto;
  .custom-file-input {
    height: 110px;
  }
  .custom-file-label {
    height: 110px;
    background: rgba(197, 208, 222, 0.1);
    border: 1px dashed #c5d0de;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    &:after {
      display: none;
    }
    .form-file-text {
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      color: #646a87;
      &::before {
        display: block;
        content: '';
        height: 34px;
        margin-bottom: 6px;
        background: url('../../../../assets/icons/drag_and_drop.png') no-repeat scroll;
        background-position: center center;
      }
    }
    &.dragging {
      background: rgba(197, 208, 222, 0.1);
      border: 1px dashed #2260ff;
      box-shadow: -4px -4px 16px rgba(34, 96, 255, 0.08), 4px 4px 16px rgba(34, 96, 255, 0.08);
      .form-file-text {
        font-weight: 500;
        font-size: 18px;
        line-height: 27px;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #2260ff;
        margin-bottom: 0;
        &::before {
          display: none;
        }
      }
    }
  }
}
</style>
